import { CallQueueType } from "@/constants/queue";
import { SectionType } from "@/constants/section";
import { WIDGET_IDS } from "@/constants/widget";
import { useAppData, UserRole } from "@/contexts/AppDataContext";
import { useDashboardData } from "@/contexts/DashboardContext";
import { useWidgetsData } from "@/hooks/api/useWidgetsData";
import { useFilterCache } from "@/hooks/useFilterCache";
import { SpecificDateRange } from "@/lib/axios/types/widgets.type";
import { DateRangeChange, FilterChangeEvent, QuickRange as FilterQuickRange, WidgetType } from "@/types";
import { calculateDateRange, DATE_RANGE_KEYS, DateRangeValue, getRecalculatedDatesIfNeeded, PERIODIC_QUICK_RANGES } from "@/utils/dateRangeUtils";
import { getMaxDayOfMonth } from "@/utils/dateUtils";
import { add, differenceInMonths, endOfWeek, sub } from "date-fns";
import React from "react";
import FilterBar from "../../common/FilterBar";
import {
  AverageHandlingTimeTrendWidget,
  AverageWaitingTimeTrendWidget,
  CallVolumeTrendLineChart,
  SlaTrendWidget
} from "../../widgets/trends-insights";
import { getAgentIds } from "@/utils/agentUtils";

const widgetIds = [
  WIDGET_IDS.CALL_VOLUME_TREND,        // 25 - CallVolumeTrendWidget
  WIDGET_IDS.AVERAGE_WAITING_TIME_TREND, // 36 - AverageWaitingTimeTrendWidget
  WIDGET_IDS.AVERAGE_HANDLING_TIME_TREND, // 27 - AverageHandlingTimeTrendWidget
  WIDGET_IDS.SLA_TREND,                // 28 - SlaTrendWidget
];

interface TrendsSectionProps {
  isGeneralMode: boolean;
}

const TrendsSection: React.FC<TrendsSectionProps> = ({ isGeneralMode }) => {
  const defaultDateRangeKey = DATE_RANGE_KEYS.MONTHLY;
  const { current } = useDashboardData();
  const { filters, saveFilters } = useFilterCache("trends-insights", current?.id);
  const { directAgentGroupIds, queueMicrosoftIds, uniqueAgents, userRole } = useAppData();

  const agentIds = React.useMemo(() => getAgentIds(uniqueAgents), [uniqueAgents]);

  const getArrayOrAll = (array: string[]) => array?.length > 0 ? array : queueMicrosoftIds;
  
  const getQueueMicrosoftIds = (selectedQueueType: CallQueueType, queues: string[]) => {
    if (userRole === UserRole.ADMIN && queues.length === 0) {
      return null;
    }
    return selectedQueueType === CallQueueType.CALL_QUEUE ? getArrayOrAll(queues) : [];
  };
  
  const [dateRange, setDateRange] = React.useState<DateRangeChange>({
    key: filters?.dateRangeKey ?? defaultDateRangeKey,
    startDate: null,
    endDate: null,
    previousStartDate: null,
    previousEndDate: null,
  });

  const mappingPeriodicQuickRanges = (key: string): SpecificDateRange[] => {
    let dateRangeCalculated: DateRangeValue = null;
    switch (key) {
      case DATE_RANGE_KEYS.WEEKLY:
        dateRangeCalculated = calculateDateRange(DATE_RANGE_KEYS.THIS_WEEK);
        break;
      case DATE_RANGE_KEYS.MONTHLY:
        dateRangeCalculated = calculateDateRange(DATE_RANGE_KEYS.THIS_MONTH);
        break;
      case DATE_RANGE_KEYS.QUARTERLY:
        dateRangeCalculated = calculateDateRange(DATE_RANGE_KEYS.THIS_QUARTER);
        break;
      case DATE_RANGE_KEYS.YEARLY:
        dateRangeCalculated = calculateDateRange(DATE_RANGE_KEYS.THIS_YEAR);
        break;
      default:
        return null;
    }
    let slaByCQPrevious: { previousFrom: Date; previousTo: Date } = null;
    const { previousFrom, previousTo } = calculateSpecificPreviousDateRange(dateRangeCalculated.from, dateRangeCalculated.to, key);


    const callVolumeTrendPrevious = calculateSpecificPreviousDateRange(dateRangeCalculated.from, dateRangeCalculated.to, key, true);

    // Calculate previous date range for SLA by call queue for quarterly
    if (key === DATE_RANGE_KEYS.QUARTERLY) {
      slaByCQPrevious = calculateSpecificPreviousDateRange(dateRangeCalculated.from, dateRangeCalculated.to, DATE_RANGE_KEYS.LAST_QUARTERS);
    }

    const specificDateRanges = [
      {
        widgetIds: [WIDGET_IDS.CALL_VOLUME_TREND],
        startDate: dateRangeCalculated.from.toISOString(),
        endDate: dateRangeCalculated.to.toISOString(),
        previousStartDate: callVolumeTrendPrevious?.previousFrom.toISOString(),
        previousEndDate: callVolumeTrendPrevious?.previousTo.toISOString(),
      },
      {
        widgetIds: [WIDGET_IDS.SLA_BY_CALL_QUEUE],
        startDate: dateRangeCalculated.from.toISOString(),
        endDate: dateRangeCalculated.to.toISOString(),
        previousStartDate: (slaByCQPrevious?.previousFrom ?? previousFrom).toISOString(),
        previousEndDate: (slaByCQPrevious?.previousTo ?? previousTo).toISOString(),
      }
    ]
    return specificDateRanges;
  }

  const calculateSpecificPreviousDateRange = (from: Date, to: Date, key: string, fullPeriod: boolean = false): { previousFrom: Date; previousTo: Date } => {
    let previousFrom: Date;
    let previousTo: Date;

    const isPeriodicRange =
      DATE_RANGE_KEYS.WEEKLY === key ||
      DATE_RANGE_KEYS.MONTHLY === key ||
      DATE_RANGE_KEYS.QUARTERLY === key ||
      DATE_RANGE_KEYS.YEARLY === key ||
      DATE_RANGE_KEYS.LAST_QUARTERS === key;
    if (!isPeriodicRange) return null;

    switch (key) {
      case DATE_RANGE_KEYS.WEEKLY:
        previousFrom = sub(from, { weeks: 1 });
        previousTo = sub(to, { weeks: 1 });
        if (fullPeriod) {
          previousTo = endOfWeek(previousFrom, { weekStartsOn: 0 });
        }
        break;
      case DATE_RANGE_KEYS.MONTHLY:
        previousFrom = sub(from, { months: 1 });
        previousTo = sub(to, { months: 1 });
        if (fullPeriod) {
          previousTo = new Date(previousTo.getFullYear(), previousTo.getMonth(), getMaxDayOfMonth(previousTo));
        }
        break;
      case DATE_RANGE_KEYS.QUARTERLY:
        previousFrom = sub(from, { years: 1 });
        previousTo = sub(to, { years: 1 });
        if (fullPeriod) {
          previousTo = add(previousFrom, { months: 2 });
          previousTo = new Date(previousTo.getFullYear(), previousTo.getMonth(), getMaxDayOfMonth(previousTo));
        }
        break;
      case DATE_RANGE_KEYS.YEARLY:
        previousFrom = sub(from, { years: 1 });
        previousTo = sub(to, { years: 1 });

        if (fullPeriod) {
          previousTo = new Date(previousTo.getFullYear(), 11, 31);
        }
        break;
      case DATE_RANGE_KEYS.LAST_QUARTERS: {
        // Calculate the start of the current quarter
        const quarterStartMonth = Math.floor(from.getMonth() / 3) * 3;
        const currentQuarterStart = new Date(from.getFullYear(), quarterStartMonth, 1);

        // month gap between from and to
        const monthGap = differenceInMonths(to, from);
        // Calculate the start of the previous quarter
        previousFrom = sub(currentQuarterStart, { months: 3 });
        previousTo = add(previousFrom, { months: monthGap });
        // Get max day of month
        const maxDayOfMonth = getMaxDayOfMonth(previousTo);
        previousTo = new Date(previousTo.getFullYear(), previousTo.getMonth(), Math.min(maxDayOfMonth, to.getDate()));
        break;
      }
    }

    // Set time to start/end of day
    previousFrom?.setHours(0, 0, 0, 0);
    previousTo?.setHours(23, 59, 59, 999);
    return {
      previousFrom,
      previousTo
    };
  }

  // State for date range filter
  const [dateParams, setDateParams] = React.useState(() => {
    if (filters) {
      const { startDate, endDate, previousStartDate, previousEndDate } = getRecalculatedDatesIfNeeded(
        filters.dateRangeKey,
        filters
      );
      
      setDateRange({
        key: filters?.dateRangeKey,
        startDate: startDate ?? null,
        endDate: endDate ?? null,
        previousStartDate: previousStartDate ?? null,
        previousEndDate: previousEndDate ?? null,
      });
      return {
        startDate,
        endDate,
        previousStartDate,
        previousEndDate,
        widgetIds,
        queueMicrosoftIds: getQueueMicrosoftIds(filters.selectedQueueType, filters.queues),
        agentMicrosoftIds: userRole === UserRole.ADMIN ? null : agentIds,
        specificDateRanges: mappingPeriodicQuickRanges(filters.dateRangeKey),
        groupIds: filters.selectedQueueType === CallQueueType.DIRECT_AGENT_GROUP
          ? directAgentGroupIds.filter(id => filters.queues.includes(id.toString()) || filters.queues.length === 0)
          : null,
        timeStamp: new Date().toISOString()
      };
    }
    const dateRange = calculateDateRange(defaultDateRangeKey);
    return {
      startDate: dateRange.from.toISOString(),
      endDate: dateRange.to.toISOString(),
      previousStartDate: dateRange.previousFrom.toISOString(),
      previousEndDate: dateRange.previousTo.toISOString(),
      widgetIds,
      queueMicrosoftIds: userRole === UserRole.ADMIN ? null : queueMicrosoftIds,
      agentMicrosoftIds: userRole === UserRole.ADMIN ? null : agentIds,
      specificDateRanges: mappingPeriodicQuickRanges(defaultDateRangeKey),
      groupIds: null,
      timeStamp: new Date().toISOString()
    };
  });

  // Add state for current quick range key
  const [dateRangeKey, setDateRangeKey] = React.useState<string>(filters?.dateRangeKey ?? defaultDateRangeKey);

  // Refresh data when tab becomes active
  React.useEffect(() => {
    if (isGeneralMode) {
      setDateParams(prev => ({
        ...prev,
        timeStamp: new Date().toISOString()
      }));
    }
  }, [isGeneralMode]);

  const handleFilterChange = (event: FilterChangeEvent) => {
    const queues = event.queues || [];
    setDateRangeKey(event.dateRange.key);
    
    // Recalculate dates for dynamic date ranges to ensure freshness
    const recalculatedDates = getRecalculatedDatesIfNeeded(event.dateRange.key, {
      startDate: event.dateRange.startDate.toISOString(),
      endDate: event.dateRange.endDate.toISOString(),
      previousStartDate: event.dateRange.previousStartDate.toISOString(),
      previousEndDate: event.dateRange.previousEndDate.toISOString(),
    });
    
    setDateRange({
      key: event.dateRange.key,
      startDate: recalculatedDates.startDate,
      endDate: recalculatedDates.endDate,
      previousStartDate: recalculatedDates.previousStartDate,
      previousEndDate: recalculatedDates.previousEndDate,
    });
    setDateParams(prev => ({
      ...prev,
      startDate: recalculatedDates.startDate,
      endDate: recalculatedDates.endDate,
      previousStartDate: recalculatedDates.previousStartDate,
      previousEndDate: recalculatedDates.previousEndDate,
      queueMicrosoftIds: getQueueMicrosoftIds(event.selectedQueueType, event.queues),
      agentMicrosoftIds: userRole === UserRole.ADMIN ? null : agentIds,
      specificDateRanges: mappingPeriodicQuickRanges(event.dateRange.key),
      groupIds: null,
      timeStamp: new Date().toISOString()
    }));

    // persist
    saveFilters({
      dateRangeKey: event.dateRange.key,
      startDate: event.dateRange.startDate.toISOString(),
      endDate: event.dateRange.endDate.toISOString(),
      previousStartDate: event.dateRange.previousStartDate.toISOString(),
      previousEndDate: event.dateRange.previousEndDate.toISOString(),
      queues: event.queues ?? [],
      agents: event.agents ?? [],
      selectedQueueType: event.selectedQueueType
    });
  };

  // Call widgets API when dateParams changes
  const { trendsInsightsData, loading, error } = useWidgetsData(dateParams);

  const getRangeType = (key: string): 'weekly' | 'monthly' | 'quarterly' | 'yearly' => {
    switch (key) {
      case DATE_RANGE_KEYS.WEEKLY:
        return 'weekly';
      case DATE_RANGE_KEYS.MONTHLY:
        return 'monthly';
      case DATE_RANGE_KEYS.QUARTERLY:
        return 'quarterly';
      case DATE_RANGE_KEYS.YEARLY:
        return 'yearly';
      default:
        return 'weekly';
    }
  };

  return (
    <div className="w-full">
      <FilterBar
        onFilterChange={handleFilterChange}
        initialDateRangeKey={dateRangeKey}
        initialSelectedQueues={filters?.queues}
        initialSelectedAgents={filters?.agents}
        initialSelectedQueueType={filters?.selectedQueueType}
        quickRanges={PERIODIC_QUICK_RANGES as unknown as FilterQuickRange[]}
        initialDateRange={dateRange}
        sectionType={SectionType.TRENDS}
      />
      <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-4">
        <CallVolumeTrendLineChart
          data={{ id: 'call-volume-trend', type: WidgetType.CALL_VOLUMES, title: 'Call Volume Trend', value: trendsInsightsData?.callVolumeTrend ?? [] }}
          isGeneralMode={isGeneralMode}
          loading={loading}
          error={error}
          rangeType={getRangeType(dateRangeKey)}
        />
        <SlaTrendWidget
          data={{ id: 'sla-trend', type: WidgetType.AGENT_AVAILABILITY, title: 'SLA (%) Trend', value: trendsInsightsData?.slaTrend ?? [] }}
          isGeneralMode={isGeneralMode}
          loading={loading}
          error={error}
          rangeType={getRangeType(dateRangeKey)}
        />
         <AverageWaitingTimeTrendWidget
          data={{ id: 'average-waiting-time-trend', type: WidgetType.AVG_WAITING_TIME, title: 'Average Wait Time Trend', value: trendsInsightsData?.averageWaitingTimeTrend ?? [] }}
          isGeneralMode={isGeneralMode}
          loading={loading}
          error={error}
          rangeType={getRangeType(dateRangeKey)}
        /> 
        <AverageHandlingTimeTrendWidget
          data={{ id: 'average-handling-time-trend', type: WidgetType.AVG_HANDLING_TIME, title: 'Average Handle Time Trend', value: trendsInsightsData?.averageHandlingTimeTrend ?? [] }}
          isGeneralMode={isGeneralMode}
          loading={loading}
          error={error}
          rangeType={getRangeType(dateRangeKey)}
        />
      </div>
    </div>
  );
};

export default TrendsSection;
